export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Rect extends Point, Size {}

/** Pijl van de staart (`from`) naar de punt (`to`). */
export interface Arrow {
  from: Point;
  to: Point;
}

/** Pijlpunt: de punt, de twee achterhoeken en het aanzetpunt van de schacht. */
export interface ArrowHead {
  tip: Point;
  left: Point;
  right: Point;
  base: Point;
}

/**
 * Kleinste selectie die als uitsnede, markering of snip-kader telt, in de
 * pixel-eenheid van de aanroeper (bron- of CSS-pixels).
 */
export const MIN_SELECTION_PX = 10;

const MARK_STROKE_BASE = 4;
const MARK_STROKE_MIN = 3;
const MARK_STROKE_REFERENCE_WIDTH = 1920;
/** Lengte van de pijlpunt als veelvoud van de lijndikte, met een ondergrens. */
const ARROW_HEAD_LENGTH_FACTOR = 4.5;
const ARROW_HEAD_MIN_LENGTH = 10;
/** Halve breedte van de pijlpunt ten opzichte van haar lengte. */
const ARROW_HEAD_HALF_WIDTH_RATIO = 0.55;

/** Rechthoek tussen twee hoekpunten, ongeacht de sleeprichting. */
export function normalizeRect(a: Point, b: Point): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

/** Beperkt een rechthoek tot `bounds` (oorsprong 0,0). */
export function clampRect(rect: Rect, bounds: Size): Rect {
  const x = clamp(rect.x, 0, bounds.width);
  const y = clamp(rect.y, 0, bounds.height);
  return {
    x,
    y,
    width: clamp(rect.x + rect.width, 0, bounds.width) - x,
    height: clamp(rect.y + rect.height, 0, bounds.height) - y,
  };
}

/**
 * Vertaalt een sleep in weergavepixels (start/end, relatief aan het canvas) naar
 * een afgeronde rechthoek in bronpixels binnen `bounds`. `scale` = bronbreedte /
 * weergavebreedte. Onder de minimumgrootte: undefined.
 */
export function toSourceRect(start: Point, end: Point, scale: number, bounds: Size): Rect | undefined {
  const source = normalizeRect(
    { x: start.x * scale, y: start.y * scale },
    { x: end.x * scale, y: end.y * scale },
  );
  const clamped = clampRect(source, bounds);
  const rect: Rect = {
    x: Math.round(clamped.x),
    y: Math.round(clamped.y),
    width: Math.round(clamped.width),
    height: Math.round(clamped.height),
  };
  if (rect.width < MIN_SELECTION_PX || rect.height < MIN_SELECTION_PX) return undefined;
  return rect;
}

/** Punt in weergavepixels naar een afgerond punt in bronpixels binnen `bounds`. */
export function toSourcePoint(point: Point, scale: number, bounds: Size): Point {
  return {
    x: Math.round(clamp(point.x * scale, 0, bounds.width)),
    y: Math.round(clamp(point.y * scale, 0, bounds.height)),
  };
}

/**
 * Vertaalt een sleep in weergavepixels naar een pijl in bronpixels: het
 * startpunt is de staart, het eindpunt de punt. Korter dan de minimumgrootte
 * (een klik of trilling): undefined.
 */
export function toSourceArrow(start: Point, end: Point, scale: number, bounds: Size): Arrow | undefined {
  const from = toSourcePoint(start, scale, bounds);
  const to = toSourcePoint(end, scale, bounds);
  if (Math.hypot(to.x - from.x, to.y - from.y) < MIN_SELECTION_PX) return undefined;
  return { from, to };
}

/**
 * Geometrie van de pijlpunt voor een lijndikte: een gevulde driehoek op de
 * punt, nooit langer dan de pijl zelf. De schacht stopt op `base`, zodat de
 * afgeronde lijnkap niet door de punt heen steekt.
 */
export function arrowHeadGeometry(arrow: Arrow, lineWidth: number): ArrowHead {
  const dx = arrow.to.x - arrow.from.x;
  const dy = arrow.to.y - arrow.from.y;
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  const headLength = Math.min(length, Math.max(ARROW_HEAD_MIN_LENGTH, lineWidth * ARROW_HEAD_LENGTH_FACTOR));
  const halfWidth = headLength * ARROW_HEAD_HALF_WIDTH_RATIO;
  const base = { x: arrow.to.x - ux * headLength, y: arrow.to.y - uy * headLength };
  return {
    tip: { ...arrow.to },
    left: { x: base.x - uy * halfWidth, y: base.y + ux * halfWidth },
    right: { x: base.x + uy * halfWidth, y: base.y - ux * halfWidth },
    base,
  };
}

/** Lijndikte van het markeerkader, schalend met de bronbreedte. */
export function markStrokeWidth(sourceWidth: number): number {
  return Math.max(MARK_STROKE_MIN, Math.round((MARK_STROKE_BASE * sourceWidth) / MARK_STROKE_REFERENCE_WIDTH));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
